/**
 * recordingPolicy (Welle 7e / pre106) — mandantenweite Aufzeichnungs-Policy.
 *
 * Editierbare Themenliste; je Thema eigener Consent-Modus (Hinweis/Einwilligung) UND
 * Ablage-Modus (lokaler Ordner / Server-Relay). Master-Schalter, Empfänger-Public-Key und
 * Aufbewahrungsdauer gelten mandantenweit.
 *
 * Gespeichert in tenant.branding.recordingPolicy (migrationsfrei). Fallback ist bewusst
 * PERMISSIV, damit ein fehlender/alter Endpunkt das bestehende Verhalten nicht bricht.
 */

import { apiFetch } from '@/lib/auth';

export type ConsentMode = 'hinweis' | 'einwilligung';
export type DeliveryMode = 'local-folder' | 'server-relay';

export interface RecordingTopic {
  id: string;
  label: string;
  enabled: boolean;
  consentMode: ConsentMode;
  deliveryMode: DeliveryMode;
}

export interface RecordingPolicy {
  enabled: boolean;
  topics: RecordingTopic[];
  recipientPublicKey: string | null;
  retentionYears: number;
}

export function slugify(s: string): string {
  return String(s || '').toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48) || 'thema';
}

export const DEFAULT_TOPICS: RecordingTopic[] = [
  { id: 'geldanlage', label: 'Geldanlage', enabled: true, consentMode: 'hinweis', deliveryMode: 'local-folder' },
  { id: 'kredit', label: 'Kredit', enabled: true, consentMode: 'einwilligung', deliveryMode: 'local-folder' },
  { id: 'baufinanzierung', label: 'Baufinanzierung', enabled: true, consentMode: 'einwilligung', deliveryMode: 'local-folder' },
];

export const DEFAULT_POLICY: RecordingPolicy = {
  enabled: true,
  topics: DEFAULT_TOPICS,
  recipientPublicKey: null,
  retentionYears: 5,
};

function normTopic(raw: any, fallbackConsent: ConsentMode, fallbackDelivery: DeliveryMode): RecordingTopic | null {
  if (!raw || typeof raw !== 'object') return null;
  const label = typeof raw.label === 'string' && raw.label.trim() ? raw.label.trim() : '';
  const id = (typeof raw.id === 'string' && raw.id.trim()) ? slugify(raw.id) : slugify(label);
  if (!label && !raw.id) return null;
  return {
    id,
    label: label || id,
    enabled: raw.enabled !== false,
    consentMode: raw.consentMode === 'einwilligung' ? 'einwilligung' : raw.consentMode === 'hinweis' ? 'hinweis' : fallbackConsent,
    deliveryMode: raw.deliveryMode === 'server-relay' ? 'server-relay' : 'local-folder',
  };
}

export function normalizePolicy(raw: any): RecordingPolicy {
  const p = (raw && typeof raw === 'object') ? raw : {};
  const fbConsent: ConsentMode = p.consentMode === 'einwilligung' ? 'einwilligung' : 'hinweis';
  const fbDelivery: DeliveryMode = p.deliveryMode === 'server-relay' ? 'server-relay' : 'local-folder';

  let topics: RecordingTopic[];
  if (Array.isArray(p.topics)) {
    topics = p.topics.map((t: any) => normTopic(t, fbConsent, fbDelivery)).filter(Boolean) as RecordingTopic[];
  } else if (p.topics && typeof p.topics === 'object') {
    // Abwärtskompatibel: altes Schema { geldanlage:bool, kredit:bool, baufinanzierung:bool }
    topics = DEFAULT_TOPICS.map((d) => ({ ...d, enabled: p.topics[d.id] !== false, consentMode: d.consentMode, deliveryMode: fbDelivery }));
  } else {
    topics = DEFAULT_TOPICS.map((d) => ({ ...d }));
  }
  // Doppelte IDs entschärfen
  const seen = new Set<string>();
  for (const t of topics) { let id = t.id; let n = 2; while (seen.has(id)) id = `${t.id}-${n++}`; t.id = id; seen.add(id); }

  return {
    enabled: typeof p.enabled === 'boolean' ? p.enabled : true,
    topics: topics.length ? topics : DEFAULT_TOPICS.map((d) => ({ ...d })),
    recipientPublicKey: typeof p.recipientPublicKey === 'string' && p.recipientPublicKey.trim() ? p.recipientPublicKey : null,
    retentionYears: Number.isFinite(p.retentionYears) ? Math.max(1, Math.min(30, p.retentionYears)) : 5,
  };
}

let cache: RecordingPolicy = DEFAULT_POLICY;
export function cachedPolicy(): RecordingPolicy { return cache; }

export async function fetchRecordingPolicy(): Promise<RecordingPolicy> {
  try {
    const res = await apiFetch('/api/recording/policy', {}, { noLogoutOn401: true } as any);
    if (res.ok) { cache = normalizePolicy(await res.json()); }
  } catch { /* permissiver Fallback bleibt */ }
  return cache;
}

export function getTopic(p: RecordingPolicy, topicId?: string): RecordingTopic | undefined {
  if (!topicId) return undefined;
  return p.topics.find((t) => t.id === topicId);
}

/** Thema ist laut Policy aufzeichenbar (Master an + Thema vorhanden + aktiv)? */
export function policyAllowsTopic(p: RecordingPolicy, topicId?: string): boolean {
  if (!p.enabled) return false;
  const t = getTopic(p, topicId);
  return !!t && t.enabled;
}

/** Aktive, aufzeichenbare Themen (für die Auswahl im Consent-Dialog). */
export function enabledTopics(p: RecordingPolicy): RecordingTopic[] {
  return p.enabled ? p.topics.filter((t) => t.enabled) : [];
}

/** Ablage-Adapter für ein Thema. */
export function deliveryAdapterFor(p: RecordingPolicy, topicId: string): 'http-api' | 'folder' {
  const t = getTopic(p, topicId);
  return t?.deliveryMode === 'server-relay' ? 'http-api' : 'folder';
}
